"use client";

import React, { useEffect, useRef, useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import { toast } from "sonner";

import { submitReport } from "@demo/libs/api/reportApi";
import { capturePageScreenshot } from "@demo/libs/screenshot";
import type { ReportMode, ReportPayload } from "@demo/server/api/contracts/report";
import { useIsDevModeActive } from "@demo/store/dev-mode";
import useSessionStore from "@demo/store/session";

import { ReportDialog } from "./ReportDialog";

type Feedback = "win" | "failure";

/**
 * A mode's snapshot of the run being rated, captured at submit time. Modes
 * supply this via {@link FeedbackButtonsProps.getContext} so the feedback
 * feature itself stays free of mode-specific store reads.
 */
export interface FeedbackContext {
  mode: ReportMode;
  /** Machine-readable run snapshot — lands as request.json in the Slack thread. */
  requestBody: unknown;
  /** Human-readable output preview rendered in the Slack parent message. */
  modelResponse: string;
  /** Media to attach (allowlisted S3/CDN URLs). */
  mediaUrls: string[];
}

interface FeedbackButtonsProps {
  /** Snapshot the current run for the report; called when the user submits. */
  getContext: () => FeedbackContext;
  /**
   * Identity of the result being rated. `null` hides the buttons and resets
   * the submitted state (a new run clears it before producing a new value).
   */
  resultKey: string | null;
}

const buildPayload = (context: FeedbackContext, kind: Feedback, userMessage: string): ReportPayload => ({
  kind,
  mode: context.mode,
  userMessage,
  requestBody: context.requestBody,
  modelResponse: context.modelResponse,
  mediaUrls: context.mediaUrls,
  sessionId: useSessionStore.getState().sessionUuid,
  timestamp: new Date().toISOString(),
});

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ getContext, resultKey }) => {
  const isDevMode = useIsDevModeActive();

  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [submittingWin, setSubmittingWin] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Reset feedback when the rated result goes away (a new run clears it).
  const prevResultKey = useRef(resultKey);
  useEffect(() => {
    if (prevResultKey.current !== resultKey) {
      prevResultKey.current = resultKey;
      if (!resultKey) setFeedback(null);
    }
  }, [resultKey]);

  if (!isDevMode) return null;
  if (!resultKey) return null;

  const handleThumbsUp = async () => {
    if (submittingWin || feedback) return;
    setSubmittingWin(true);
    try {
      const screenshot = await capturePageScreenshot();
      const result = await submitReport({ payload: buildPayload(getContext(), "win", ""), screenshot });
      if (result.partial) {
        toast.success(formatString(strings.report.toasts.sentPartial, result.failed.length));
      } else {
        toast.success(strings.report.toasts.winSent);
      }
      setFeedback("win");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : strings.report.toasts.failed);
    } finally {
      setSubmittingWin(false);
    }
  };

  const showOnly = (option: Feedback) => feedback !== null && feedback !== option;

  return (
    <>
      <div className="flex items-center gap-1 text-foreground-primary">
        {!showOnly("win") && (
          <Button
            aria-label={strings.report.thumbsUpAriaLabel}
            aria-pressed={feedback === "win"}
            className={cn("size-8 p-0", feedback === "win" && "text-primary")}
            disabled={submittingWin || feedback !== null}
            size="sm"
            variant="ghost"
            onClick={handleThumbsUp}
          >
            <Icon className={cn("size-4", feedback === "win" && "fill-current")} name="thumbs-up" />
          </Button>
        )}
        {!showOnly("failure") && (
          <Button
            aria-label={strings.report.thumbsDownAriaLabel}
            aria-pressed={feedback === "failure"}
            className={cn("size-8 p-0", feedback === "failure" && "text-primary")}
            disabled={submittingWin || feedback !== null}
            size="sm"
            variant="ghost"
            onClick={() => setDialogOpen(true)}
          >
            <Icon className={cn("size-4", feedback === "failure" && "fill-current")} name="thumbs-down" />
          </Button>
        )}
      </div>
      <ReportDialog
        buildPayload={(message) => buildPayload(getContext(), "failure", message)}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmitted={() => setFeedback("failure")}
      />
    </>
  );
};
